import {
  TeamMemberTaskCategory,
  TeamMemberTaskPriority,
  type TaskDateTime,
  type TeamMemberTaskMetadata,
  type TeamMemberTaskRecurrence,
  type TeamMemberTaskReminder,
} from "@fitness-app/data-models/entities/TeamMemberTask";

export type { TaskDateTime };

export interface TraineeOption {
  id: string;
  firstName: string;
  lastName: string | null;
  email?: string | null;
}

export interface TeamMemberTaskFormModel {
  title: string;
  description?: string | null;
  dueDate?: TaskDateTime | null;
  startDate?: TaskDateTime | null;
  endDate?: TaskDateTime | null;
  priority: TeamMemberTaskPriority;
  category: TeamMemberTaskCategory;
  traineeId?: string | null;
  trainee?: TraineeOption | null; // For display purposes
  reminders?: TeamMemberTaskReminder[];
  recurrence?: TeamMemberTaskRecurrence | null;
  metadata?: TeamMemberTaskMetadata | null;
  tags?: string[];
}

// Convert TaskDateTime to ISO string for storage
export const taskDateTimeToString = (dt: TaskDateTime | null | undefined): string | null => {
  if (!dt?.date) return null;
  if (dt.time) {
    return `${dt.date}T${dt.time}:00`;
  }
  return dt.date;
};

// Convert ISO string to TaskDateTime for form
export const stringToTaskDateTime = (
  str: string | null | undefined,
  timezone = "Europe/Warsaw",
): TaskDateTime | null => {
  if (!str) return null;

  // Check if it's just a date (YYYY-MM-DD) or datetime
  if (str.length === 10) {
    return { date: str, time: null, timezone };
  }

  // Extract date and time from ISO string
  const date = str.slice(0, 10);
  const time = str.slice(11, 16);
  const hasTime = time && time !== "00:00";

  return { date, time: hasTime ? time : null, timezone };
};

export const categoryOptions = [
  { value: TeamMemberTaskCategory.TrainingPlan, label: "Plan treningowy" },
  { value: TeamMemberTaskCategory.NutritionPlan, label: "Plan żywieniowy" },
  { value: TeamMemberTaskCategory.ClientCommunication, label: "Komunikacja z klientem" },
  { value: TeamMemberTaskCategory.Measurements, label: "Pomiary" },
  { value: TeamMemberTaskCategory.ProgressReview, label: "Przegląd postępów" },
  { value: TeamMemberTaskCategory.ProgramRenewals, label: "Odnowienia programu" },
  { value: TeamMemberTaskCategory.Other, label: "Inne" },
];

export const priorityOptions = [
  { value: TeamMemberTaskPriority.Low, label: "Niski", color: "#9CA3AF" },
  { value: TeamMemberTaskPriority.Medium, label: "Średni", color: "#3B82F6" },
  { value: TeamMemberTaskPriority.High, label: "Wysoki", color: "#F97316" },
  { value: TeamMemberTaskPriority.Urgent, label: "Pilny", color: "#EF4444" },
];

export const priorityColors: Record<TeamMemberTaskPriority, string> = {
  [TeamMemberTaskPriority.Low]: "#9CA3AF",
  [TeamMemberTaskPriority.Medium]: "#3B82F6",
  [TeamMemberTaskPriority.High]: "#F97316",
  [TeamMemberTaskPriority.Urgent]: "#EF4444",
};

export const priorityBorderColors: Record<TeamMemberTaskPriority, string> = {
  [TeamMemberTaskPriority.Low]: "border-l-gray-400",
  [TeamMemberTaskPriority.Medium]: "border-l-blue-500",
  [TeamMemberTaskPriority.High]: "border-l-orange-500",
  [TeamMemberTaskPriority.Urgent]: "border-l-red-500",
};

export const categoryColors: Record<TeamMemberTaskCategory, string> = {
  [TeamMemberTaskCategory.TrainingPlan]: "blue",
  [TeamMemberTaskCategory.NutritionPlan]: "green",
  [TeamMemberTaskCategory.ClientCommunication]: "purple",
  [TeamMemberTaskCategory.Measurements]: "cyan",
  [TeamMemberTaskCategory.ProgressReview]: "orange",
  [TeamMemberTaskCategory.ProgramRenewals]: "gold",
  [TeamMemberTaskCategory.Other]: "default",
};

export const getCategoryLabel = (category: TeamMemberTaskCategory): string => {
  return categoryOptions.find((opt) => opt.value === category)?.label ?? category;
};

export const getPriorityLabel = (priority: TeamMemberTaskPriority): string => {
  return priorityOptions.find((opt) => opt.value === priority)?.label ?? priority;
};

export const getPriorityColor = (priority: TeamMemberTaskPriority): string => {
  return priorityColors[priority] || "#9CA3AF";
};
